import { usesPatientPageV2 } from "../util/betaFeatures";
import { routes } from "../router/mobile/routes";

/**
 * UI base paths for the patient profile page.
 *
 * The legacy profile route (`routes.patientProfile`:
 * `/patient-profile/:patientId`) is the default. Users with the
 * `patient-page-v2` beta feature use the tabbed patient page
 * (`routes.patient`: `/patient/:patientId/:tab?`).
 *
 * Prefer getPatientPage() below. These constants exist for flows that must
 * target one platform's page explicitly (e.g. mobile-only location.state
 * handling, home-health route locking).
 */

/**
 * Canonical patient-page tab names. These are the desktop path segments
 * (the `allTabs` keys in src/pages/Patient/index.tsx); the mobile
 * PatientProfile selects tabs via a `?tab=` query param instead, using the
 * keys mapped below.
 */
export type PatientPageTab =
  | "overview"
  | "vitals"
  | "iq"
  | "flags"
  | "visits"
  | "bed-exits"
  | "notes"
  | "ehr"
  | "diagnosis"
  | "med-rec";

/** Mobile `?tab=` query keys (`Tabs.TabPane key=` values in
 *  src/pages/PatientProfile/overview/index.tsx) by canonical tab name. */
const MOBILE_TAB_QUERY_KEY: Partial<Record<PatientPageTab, string>> = {
  vitals: "circadiaC200DataTab",
  iq: "circadiaIQSummaryTab",
  flags: "circadiaClinicalTab",
  visits: "circadiaVisitsTab",
  "bed-exits": "circadiaMotionTab",
  notes: "circadiaProgressNotesTab",
  ehr: "ehrDataTab",
  "med-rec": "medicationsTab",
};

/**
 * Returns the patient page path for the current user, optionally targeting
 * a specific tab.
 *
 * - Beta redesign on (`patient-page-v2` beta feature):
 *   `/patient/{patientId}` (+ `/<tab>` path segment)
 * - Default / feature off:
 *   `/patient-profile/{patientId}` (+ `?tab=<legacy key>`)
 *
 * Resolved at call time (not module load) because the beta-feature list is
 * synced from getUserBetaFeatures after login and can change from Settings.
 */
export const getPatientPage = (
  patientId: number | string,
  tab?: PatientPageTab
): string => {
  const id = String(patientId);
  if (usesPatientPageV2()) {
    return routes.patient.path
      .replace(":patientId", id)
      .replace("/:tab?", tab ? `/${tab}` : "");
  }
  const path = routes.patientProfile.path.replace(":patientId", id);
  const mobileTabKey = tab && MOBILE_TAB_QUERY_KEY[tab];
  return mobileTabKey ? `${path}?tab=${mobileTabKey}` : path;
};

/**
 * Canonical patient-page tabs that exist on the Compassus patient chart, by
 * the tab key that chart uses (`allTabs` in src/pages/CompassusPatient).
 *
 * Only the overlap is listed. A canonical tab with no Compassus equivalent
 * (iq, bed-exits, notes, ehr, diagnosis, med-rec) resolves to no tab segment,
 * which the Compassus chart renders as `overview` — better than deep-linking
 * to a tab that page would refuse to render.
 */
const COMPASSUS_TAB_BY_PATIENT_PAGE_TAB: Partial<
  Record<PatientPageTab, string>
> = {
  overview: "overview",
  vitals: "vitals",
  flags: "flags",
  visits: "visits",
};

/**
 * Returns the patient page path for a clinic, honouring which patient chart
 * that clinic actually runs.
 *
 * HH RPM clinics run the Compassus patient chart
 * (`/compassus-patient/:patientId/:tab?`), which renders a different tab set
 * off different data than the SNF patient page. Surfaces shared by both — the
 * Inbox above all — must route by clinic rather than assuming the SNF page.
 *
 * Every other clinic keeps getPatientPage() and its `patient-page-v2`
 * branching, unchanged.
 */
export const getPatientPageForClinic = (
  patientId: number | string,
  tab: PatientPageTab | undefined,
  { isHhRpmClinic }: { isHhRpmClinic: boolean }
): string => {
  if (!isHhRpmClinic) {
    return getPatientPage(patientId, tab);
  }

  const compassusTab = tab && COMPASSUS_TAB_BY_PATIENT_PAGE_TAB[tab];
  return routes.compassusPatient.path
    .replace(":patientId", String(patientId))
    .replace("/:tab?", compassusTab ? `/${compassusTab}` : "");
};
